import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import PageTitle from "../../../jsx/layouts/PageTitle";
import { getUddyamaanApplicationById } from "../../../services/adminApiService/uddyamaanApi/uddyamaanApi";

const DOC_LABEL = {
  domicile_certificate: "Domicile Certificate",
  birth_certificate: "Birth Certificate or a certificate from Registrar of Births and Deaths Office / Certification from a school",
  aadhaar: "Aadhaar Card",
  id_doc_front: "Identity Document (Front)",
  id_doc_back: "Identity Document (Back)",
  photo: "Applicant Photo",
};

const ID_DOC_LABEL = {
  aadhaar: "Aadhaar Card",
  pan_card: "PAN Card",
  driving_licence: "Driving Licence",
  school_marksheet: "School Marksheet",
};

const SCHEME_LABEL = { uddyamaan: 'Udiyamaan Unnayan Yojna', protsaahan: 'Protsaahan' };

const UddyamaanUnnayanAssessment = ({ schemeType = 'uddyamaan' }) => {
  const { id } = useParams();
  const schemeLabel = SCHEME_LABEL[schemeType] || SCHEME_LABEL.uddyamaan;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getUddyamaanApplicationById(id);
        if (res?.status === 403) {
          setError(403);
          setData(null);
        } else {
          setData(res?.data?.data || null);
        }
      } catch (err) {
        console.error(err);
        setError("error");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <>
        <PageTitle activeMenu="Application Detail" motherMenu={schemeLabel} />
        <div className="container py-5 text-center text-muted">
          {error === 403
            ? "You don't have access to this application."
            : "Application not found."}
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Application Detail" motherMenu={schemeLabel} />
      <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h3 className="text-primary mb-0">Application Detail</h3>
        </div>

        {data.admin_remark && (
          <div className={`alert ${data.decision_made_at ? (data.status === "rejected" ? "alert-danger" : data.status === "revision_requested" ? "alert-warning" : "alert-success") : "alert-info"} mb-4`}>
            <strong>Admin Remark: </strong>{data.admin_remark}
          </div>
        )}

        {/* Program Info */}
        <div className="border rounded mb-4 p-3" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          <h6 className="mb-2" style={{ color: "#6366F1", fontWeight: 600 }}>Program</h6>
          <p className="mb-0 fw-semibold">{data.program?.title || "—"}</p>
        </div>

        {/* Applicant Details */}
        <div className="border rounded mb-4 p-3" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          <h6 className="mb-3" style={{ color: "#6366F1", fontWeight: 600 }}>Applicant Details</h6>
          <Row className="g-3">
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>Full Name</label>
              <input className="form-control" value={data.name || ""} readOnly disabled />
            </Col>
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>Date of Birth</label>
              <input className="form-control" value={data.dob ? new Date(data.dob).toLocaleDateString("en-IN") : ""} readOnly disabled />
            </Col>
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>Gender</label>
              <input className="form-control" value={data.gender || ""} readOnly disabled />
            </Col>
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>Standing Height</label>
              <input className="form-control" value={data.height_cm ? `${data.height_cm} cm` : ""} readOnly disabled />
            </Col>
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>Body Weight</label>
              <input className="form-control" value={data.weight_kg ? `${data.weight_kg} kg` : ""} readOnly disabled />
            </Col>
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>Mother/Guardian Name</label>
              <input className="form-control" value={data.mother_guardian_name || ""} readOnly disabled />
            </Col>
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>Class/Grade</label>
              <input className="form-control" value={data.class_grade || ""} readOnly disabled />
            </Col>
            {data.sport && (
              <Col md={4}>
                <label className="form-label" style={{ fontWeight: 500 }}>Sport</label>
                <input className="form-control" value={data.sport || ""} readOnly disabled />
              </Col>
            )}
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>Identity Document</label>
              <input className="form-control" value={ID_DOC_LABEL[data.id_doc_type] || data.id_doc_type || ""} readOnly disabled />
            </Col>
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>ID Number</label>
              <input className="form-control" value={data.aadhaar_number || ""} readOnly disabled />
            </Col>
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>District</label>
              <input className="form-control" value={data.district || ""} readOnly disabled />
            </Col>
            {schemeType === 'protsaahan' ? (
              <Col md={4}>
                <label className="form-label" style={{ fontWeight: 500 }}>Block</label>
                <input className="form-control" value={data.block || ""} readOnly disabled />
              </Col>
            ) : data.jurisdiction_type === "ward" ? (
              <>
                <Col md={4}>
                  <label className="form-label" style={{ fontWeight: 500 }}>Ward</label>
                  <input className="form-control" value={data.ward || ""} readOnly disabled />
                </Col>
                <Col md={4}>
                  <label className="form-label" style={{ fontWeight: 500 }}>Local Body</label>
                  <input className="form-control" value={data.local_body || ""} readOnly disabled />
                </Col>
              </>
            ) : (
              <>
                <Col md={4}>
                  <label className="form-label" style={{ fontWeight: 500 }}>Block</label>
                  <input className="form-control" value={data.block || ""} readOnly disabled />
                </Col>
                <Col md={4}>
                  <label className="form-label" style={{ fontWeight: 500 }}>Nyaya Panchayat</label>
                  <input className="form-control" value={data.nyaya_panchayat || ""} readOnly disabled />
                </Col>
              </>
            )}
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>Pincode</label>
              <input className="form-control" value={data.pincode || ""} readOnly disabled />
            </Col>
            <Col xs={12}>
              <label className="form-label" style={{ fontWeight: 500 }}>Address</label>
              <textarea className="form-control" value={data.address || ""} readOnly disabled rows={2} />
            </Col>
          </Row>
        </div>

        {/* Bank Details */}
        <div className="border rounded mb-4 p-3" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          <h6 className="mb-3" style={{ color: "#6366F1", fontWeight: 600 }}>Bank Details</h6>
          <Row className="g-3">
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>Account Holder's Name</label>
              <input className="form-control" value={data.account_holder_name || ""} readOnly disabled />
            </Col>
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>Bank Name</label>
              <input className="form-control" value={data.bank_name || ""} readOnly disabled />
            </Col>
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>Account Number</label>
              <input className="form-control" value={data.bank_account_number || ""} readOnly disabled />
            </Col>
            <Col md={4}>
              <label className="form-label" style={{ fontWeight: 500 }}>IFSC Code</label>
              <input className="form-control" value={data.ifsc_code || ""} readOnly disabled />
            </Col>
          </Row>
        </div>

        {/* Documents */}
        {data.docs && data.docs.length > 0 && (
          <div className="border rounded mb-4 p-3" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
            <h6 className="mb-3" style={{ color: "#6366F1", fontWeight: 600 }}>Uploaded Documents</h6>
            {data.docs.map((doc) => {
              const isPdf = doc.file_url?.toLowerCase().includes(".pdf");
              return (
                <div key={doc.id} className="d-flex align-items-center gap-3 mb-3 p-2 border rounded" style={{ borderRadius: 8 }}>
                  <div style={{ minWidth: 120, fontWeight: 500, fontSize: 14 }}>
                    {DOC_LABEL[doc.doc_type] || doc.doc_type}
                  </div>
                  <div className="text-muted" style={{ fontSize: 13, flexGrow: 1 }}>
                    {doc.file_name || "—"}{doc.file_size ? ` (${doc.file_size})` : ""}
                  </div>
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary"
                  >
                    {isPdf ? "View PDF" : "View Image"}
                  </a>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default UddyamaanUnnayanAssessment;
